// PropertyContext.tsx
import React, { createContext, useContext, useEffect, ReactNode, useMemo } from 'react';
import useGetPropertyDetails from '../../hooks/api/Property/useGetPropertyDetails';
import { getPropertyImages } from '../../utility/utility_functions';
import { PropertyContextType } from './DMContextInterfaces';
import { useListSortContext } from './ListSortContext';
import { useDecisionFormContext } from './DecisionFormContext';
import { initialState } from './formReducer';
import { usePropertyIdResolver } from '../../hooks/usePropertyIdResolver';
const PropertyContext = createContext<PropertyContextType | undefined>(undefined);

export function PropertyProvider({ children }: { children: ReactNode }) {
  const { propertyId, source: propertySource, setPropertyId } = usePropertyIdResolver();
  const { activeListSort } = useListSortContext();
  const { dispatch, state: { propertyId: lspjPropertyId } } = useDecisionFormContext();

  const {
    propertyDetails,
    isLoadingPropertyDetails,
    errorPropertyDetails,
  } = useGetPropertyDetails(propertyId);

  const mlsPropertyImages: string[] = useMemo(() => getPropertyImages(propertyDetails), [ propertyDetails ]);

  const sqft = propertyDetails?.sqft ?? null;

  useEffect(() => {
    if (propertyId && activeListSort?.total_properties) {
      const found = activeListSort.list_sort_property_joins.find(p => p.property_id === propertyId);
      if (found) {
        dispatch({ type: 'SET_ACTIVE_LSPJ', payload: {
          ...found,
          status: null,
          loading: false,
          error: null,
          propertyId: propertyId,
        } });
      }
      else {
        dispatch({ type: 'SET_ACTIVE_LSPJ', payload: {
          ...initialState,
          status: null,
          loading: false,
          error: null,
          propertyId: propertyId,
        } });
      }
    }
  }, [ propertyId, activeListSort ]);

  return (
    <PropertyContext.Provider
      value={{
        propertyId,
        propertyDetails,
        isLoadingPropertyDetails: isLoadingPropertyDetails || propertyId !== lspjPropertyId,
        errorPropertyDetails,
        mlsPropertyImages,
        sqft,
        propertySource,
        setPropertyId
      }}>
      {children}
    </PropertyContext.Provider>
  );
}

export function usePropertyContext() {
  const ctx = useContext(PropertyContext);
  if (!ctx) throw new Error('usePropertyContext must be used within PropertyProvider');
  return ctx;
}
